import { moduleById } from '../data/skillModules'
import { AudienceLevel, Cohort, LEVEL_LABELS } from '../types/pathway'
import { formatHours, totalHours } from './pathwayMatching'

interface WordInput {
  pathwayName: string
  company: string
  cohort: Cohort | null
  audience: AudienceLevel | 'any'
  /** Module ids in the order the prospect should take them. */
  items: string[]
  /** Optional field labels the rep ticked in the send dialog. */
  fields: string[]
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Builds a Word-compatible document. Word opens HTML with the Office
 * namespaces as an editable document, which keeps the rep's real job — pasting
 * the pathway into their own proposal template — a straight copy and paste.
 */
export function buildPathwayWord({
  pathwayName,
  company,
  cohort,
  audience,
  items,
  fields,
}: WordInput): string {
  const title = pathwayName.trim() || 'Recommended Learning Pathway'
  const recipient = company.trim() || 'your team'
  const audienceLabel = audience === 'any' ? 'Mixed' : LEVEL_LABELS[audience]
  const today = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  const showCategory = fields.includes('Category')
  const showTopics = fields.includes('Topics')
  const showModuleId = fields.includes('Skill Module ID')

  const modules = items
    .map((id) => moduleById(id))
    .filter((module): module is NonNullable<typeof module> => Boolean(module))

  const overviewRows = modules
    .map((module, index) => {
      const cells = [
        `<td style="${CELL}">${index + 1}</td>`,
        `<td style="${CELL}"><b>${escapeHtml(module.name)}</b></td>`,
      ]
      if (showCategory) {
        cells.push(`<td style="${CELL}">${escapeHtml(module.category)}</td>`)
      }
      return `<tr>${cells.join('')}</tr>`
    })
    .join('')

  const overviewHeaders = [
    `<th style="${HEAD}">#</th>`,
    `<th style="${HEAD}">Skill module</th>`,
    showCategory ? `<th style="${HEAD}">Category</th>` : '',
  ].join('')

  const sections = modules
    .map((module, index) => {
      const meta = [
        showCategory ? escapeHtml(module.category) : null,
        showModuleId ? escapeHtml(module.courseId) : null,
      ].filter(Boolean)

      return `
        <h2 style="font-size:14pt;color:#111827;margin:24pt 0 4pt 0;">
          ${index + 1}. ${escapeHtml(module.name)}
        </h2>
        ${
          meta.length
            ? `<p style="font-size:9pt;color:#6b7280;margin:0 0 8pt 0;">${meta.join(
                ' &middot; ',
              )}</p>`
            : ''
        }
        <p style="margin:0 0 6pt 0;"><b>Description</b><br/>${escapeHtml(
          module.description,
        )}</p>
        <p style="margin:0 0 6pt 0;"><b>Relevance</b><br/>${escapeHtml(
          module.relevance,
        )}</p>
        <p style="margin:0 0 2pt 0;"><b>Expected outcomes</b></p>
        <ul style="margin:0 0 6pt 0;">
          ${module.outcomes
            .map((outcome) => `<li>${escapeHtml(outcome)}</li>`)
            .join('')}
        </ul>
        ${
          showTopics
            ? `<p style="margin:0 0 6pt 0;"><b>Topics</b><br/>${escapeHtml(
                module.topics.join(' &middot; '),
              )}</p>`
            : ''
        }
      `
    })
    .join('')

  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <!--[if gte mso 9]><xml>
    <w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument>
  </xml><![endif]-->
  <style>
    @page { size: letter; margin: 1in; }
    body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #1f2937; }
  </style>
</head>
<body>
  <p style="font-size:10pt;color:#DB3725;font-weight:bold;margin:0;">
    University of Phoenix &middot; Professional Development
  </p>

  <h1 style="font-size:24pt;color:#111827;margin:12pt 0 4pt 0;">
    ${escapeHtml(title)}
  </h1>
  <p style="font-size:12pt;color:#374151;margin:0;">
    Prepared for ${escapeHtml(recipient)}${
      cohort ? ` &middot; ${escapeHtml(cohort.name)}` : ''
    }
  </p>
  <p style="font-size:9pt;color:#6b7280;margin:2pt 0 16pt 0;">${today}</p>

  <p style="margin:0 0 4pt 0;"><b>Built for:</b> ${escapeHtml(audienceLabel)}</p>
  <p style="margin:0 0 4pt 0;"><b>Modules:</b> ${modules.length}</p>
  <p style="margin:0 0 16pt 0;"><b>Total time:</b> ${formatHours(
    totalHours(items),
  )}</p>

  <p style="margin:0 0 16pt 0;">
    This pathway was built from our conversation about ${escapeHtml(
      recipient,
    )} and is listed in the order we recommend taking it.
  </p>

  <h2 style="font-size:14pt;color:#111827;margin:0 0 8pt 0;">At a glance</h2>
  <table cellspacing="0" cellpadding="6" style="border-collapse:collapse;width:100%;">
    <tr>${overviewHeaders}</tr>
    ${overviewRows}
  </table>

  <h2 style="font-size:16pt;color:#111827;margin:28pt 0 0 0;">The pathway</h2>
  ${sections}

  <p style="font-size:8pt;color:#6b7280;margin-top:28pt;">
    Prepared by University of Phoenix Professional Development. Module content
    and availability are subject to change. Contact your account team for
    enrollment and pricing.
  </p>
</body>
</html>`
}

const CELL = 'border:1px solid #d1d5db;font-size:10pt;'
const HEAD =
  'border:1px solid #d1d5db;background:#f3f4f6;font-size:9pt;text-align:left;'

export function wordFileName(pathwayName: string): string {
  const slug = (pathwayName || 'pathway')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `${slug || 'pathway'}.doc`
}

export function downloadWord(fileName: string, contents: string): void {
  const blob = new Blob([contents], { type: 'application/msword' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
